import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface CarrierTargetOption {
  value: string;
  label: string;
}

export function CarrierTargetPicker({
  ariaLabel,
  disabled = false,
  options,
  onSelect,
}: {
  ariaLabel: string;
  disabled?: boolean;
  options: CarrierTargetOption[];
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('en-US');
    if (!normalizedQuery) return options;
    return options.filter((option) => (
      option.label.toLocaleLowerCase('en-US').includes(normalizedQuery)
    ));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const choose = (option: CarrierTargetOption) => {
    onSelect(option.value);
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  return (
    <div ref={rootRef} className="relative mt-1">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-surface-400" />
        <Input
          type="text"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open && filteredOptions[activeIndex]
            ? `${listboxId}-${activeIndex}`
            : undefined}
          autoComplete="off"
          disabled={disabled}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              setQuery('');
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActiveIndex((current) => Math.min(
                current + 1,
                Math.max(filteredOptions.length - 1, 0)
              ));
              return;
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              setActiveIndex((current) => Math.max(current - 1, 0));
              return;
            }
            if (event.key === 'Enter' && open && filteredOptions[activeIndex]) {
              event.preventDefault();
              choose(filteredOptions[activeIndex]);
            }
          }}
          placeholder="Choose or search Carriers…"
          className="h-9 pl-8 pr-9 text-xs"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setOpen((current) => !current);
            setQuery('');
          }}
          aria-label={open ? 'Close Carrier list' : 'Open Carrier list'}
          className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-surface-400 hover:bg-surface-100 hover:text-surface-700 disabled:opacity-50 dark:hover:bg-surface-800 dark:hover:text-surface-200"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </button>
      </div>

      {open && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Carriers"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-surface-200 bg-white py-1 shadow-xl dark:border-surface-700 dark:bg-surface-900"
        >
          {filteredOptions.length > 0 ? filteredOptions.map((option, index) => (
            <button
              key={option.value}
              id={`${listboxId}-${index}`}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(option)}
              className={cn(
                'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs transition-colors',
                index === activeIndex
                  ? 'bg-amber-50 text-surface-950 dark:bg-amber-950/30 dark:text-white'
                  : 'text-surface-700 hover:bg-surface-50 dark:text-surface-200 dark:hover:bg-surface-800'
              )}
            >
              <span className="truncate">{option.label}</span>
              {index === activeIndex && <Check className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />}
            </button>
          )) : (
            <p className="px-3 py-5 text-center text-xs text-surface-500 dark:text-surface-400">
              No Carriers match your search.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
